import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link, useParams } from "react-router-dom";
import { ADMIN_BASE_URL } from "../config/config";
import {
  getAdminServiceStatus,
  getServiceLabel,
} from "../utils/careAccess";
import "../components/App.css";

const statusColor = {
  Active: "success",
  Expired: "warning",
  Pending: "info",
  Unpaid: "default",
};

const getPaymentModeLabel = (payment) => {
  if (payment?.paymentMethod === "qr") {
    return "QR / UPI";
  }
  if (payment?.paymentMethod === "paypal") {
    return "PayPal";
  }
  return "Card";
};

const formatPaymentAmount = (payment) => {
  if (payment?.totalAmount == null) {
    return "—";
  }

  if (payment.paymentMethod === "qr") {
    return Number(payment.totalAmount).toFixed(2);
  }

  return (Number(payment.totalAmount) / 100).toFixed(2);
};

const DetailRow = ({ label, value }) => {
  if (value == null || value === "") {
    return null;
  }

  return (
    <div className="space-between">
      <Typography sx={{ fontWeight: "bold", fontSize: "16px", mb: 1 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </div>
  );
};

const ViewFileButton = ({ url, label = "View" }) => {
  if (!url) {
    return null;
  }

  return (
    <Button
      variant="contained"
      color="success"
      size="small"
      onClick={() => window.open(url, "_blank")}
    >
      {label}
    </Button>
  );
};

const AdminPaymentById = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const { userId } = useParams();

  useEffect(() => {
    const fetchUserById = async () => {
      setLoading(true);
      setLoadError("");

      try {
        const token = localStorage.getItem("care_admin_token");
        const response = await axios.get(`${ADMIN_BASE_URL}/filter`, {
          params: { id: userId },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setUser(response.data);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setUser(null);
        setLoadError(
          error.response?.data?.msg ||
            error.response?.data?.error ||
            "Unable to load user details."
        );
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserById();
    }
  }, [userId]);

  const fullName = user
    ? [user.fName, user.mName, user.lName].filter(Boolean).join(" ")
    : "";

  return (
    <Container>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" className="admin-message-title" gutterBottom>
            User Payment Details
          </Typography>
          <Button
            component={Link}
            to="/admin/payments"
            variant="outlined"
            color="success"
            size="small"
            sx={{ mt: 1 }}
          >
            Back to all users
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress color="success" />
          </Box>
        ) : loadError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : !user ? (
          <Alert severity="info">User not found.</Alert>
        ) : (
          <>
            <Paper elevation={0} className="admin-user-summary-card">
              <Typography variant="h6" gutterBottom>
                User Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <DetailRow label="User ID:" value={user._id} />
              <DetailRow label="Name:" value={fullName} />
              <DetailRow label="Email:" value={user.email} />
              <DetailRow label="Contact:" value={user.contact} />
              <DetailRow label="Country:" value={user.country} />
              <DetailRow
                label="Location:"
                value={[user.city, user.state, user.zip].filter(Boolean).join(", ")}
              />
              <DetailRow label="Referral Code:" value={user.refcode} />
            </Paper>

            <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
              Care Plans ({user.services?.length || 0})
            </Typography>

            {!user.services?.length ? (
              <Alert severity="info">This user has no care plans yet.</Alert>
            ) : (
              user.services.map((service, index) => {
                const planStatus = getAdminServiceStatus(service);
                const payments = service.payments || [];

                return (
                  <Paper
                    key={service._id || index}
                    elevation={0}
                    className="service-details admin-plan-card"
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Box>
                        <Typography variant="h6">
                          {getServiceLabel(service)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Service ID: {service._id}
                        </Typography>
                      </Box>
                      <Chip
                        label={planStatus}
                        color={statusColor[planStatus] || "default"}
                        size="small"
                      />
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                      Plan Details
                    </Typography>
                    <DetailRow label="Care Fees:" value={`${service.careFees || "—"} ${service.currency || ""}`.trim()} />
                    <DetailRow label="Claim Amount:" value={service.careValueAmount} />
                    <DetailRow label="Care Timeline:" value={service.careExpireTimeline} />
                    <DetailRow label="Brand Name:" value={service.brandName} />
                    <DetailRow label="Product Condition:" value={service.productCondition} />
                    <DetailRow label="Bill Value:" value={service.billValue} />
                    <DetailRow label="Referral Code:" value={service.referralCode} />

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1, mb: 2 }}>
                      <ViewFileButton url={service.invoice} label="View Invoice" />
                      <ViewFileButton url={service.pictures} label="View Pictures" />
                      <ViewFileButton url={service.selfie} label="View Selfie" />
                      <ViewFileButton url={service.eventInvite} label="View Event Invite" />
                      <ViewFileButton
                        url={service.registrationDetails}
                        label="View Registration"
                      />
                      <ViewFileButton
                        url={service.establishmentPictures}
                        label="View Establishment"
                      />
                    </Box>

                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                      Payment History ({payments.length})
                    </Typography>

                    {payments.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No payments recorded for this plan.
                      </Typography>
                    ) : (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Status</TableCell>
                              <TableCell>Mode</TableCell>
                              <TableCell>Amount</TableCell>
                              <TableCell>Purchased</TableCell>
                              <TableCell>Expires</TableCell>
                              <TableCell>Transaction #</TableCell>
                              <TableCell>Receipt</TableCell>
                              <TableCell>Agreement</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {payments.map((payment, paymentIndex) => (
                              <TableRow key={`${payment.paymentId || payment.customerId}-${paymentIndex}`}>
                                <TableCell>
                                  <Chip
                                    label={payment.paymentStatus || "—"}
                                    size="small"
                                    color={
                                      payment.paymentStatus === "paid"
                                        ? "success"
                                        : payment.paymentStatus === "pending"
                                          ? "warning"
                                          : "default"
                                    }
                                  />
                                </TableCell>
                                <TableCell>{getPaymentModeLabel(payment)}</TableCell>
                                <TableCell>
                                  {formatPaymentAmount(payment)} {payment.currency || ""}
                                </TableCell>
                                <TableCell>{payment.carePurchaseDate || "—"}</TableCell>
                                <TableCell>{payment.careExpiryDate || "—"}</TableCell>
                                <TableCell>{payment.transactionNumber || "—"}</TableCell>
                                <TableCell>
                                  {payment.paymentReceiptUrl ? (
                                    <Button
                                      size="small"
                                      onClick={() =>
                                        window.open(payment.paymentReceiptUrl, "_blank")
                                      }
                                    >
                                      View
                                    </Button>
                                  ) : (
                                    "—"
                                  )}
                                </TableCell>
                                <TableCell>
                                  {payment.agreementUrl ? (
                                    <Button
                                      size="small"
                                      onClick={() =>
                                        window.open(`${payment.agreementUrl}.pdf`, "_blank")
                                      }
                                    >
                                      View
                                    </Button>
                                  ) : (
                                    "—"
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Paper>
                );
              })
            )}
          </>
        )}
    </Container>
  );
};

export default AdminPaymentById;
